import { Transition } from '@headlessui/react'
import cx from 'classnames'
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react'

import usePrevious from '../hooks/usePrevious'
import { IconProps } from '../icons/types'

export type ExternalProps = {
  /** Children of the tree node */
  children?:
    | React.ReactElement<ExternalProps>
    | React.ReactElement<ExternalProps>[]
  /** Label shown for the node */
  label: string
  /** Content preview shown for non leaf nodes. */
  preview?: string
  /** Item key. Uniquely identifies the tree item. */
  itemKey: string
  /** True if updates on the item's contents should be animated. */
  highlightUpdates?: boolean
}

export type TreeItemProps = ExternalProps &
  Partial<{
    /** Icon used for expanding an element */
    expandIcon: React.FC<IconProps>
    /** Icon used for collapsing an element */
    collapseIcon: React.FC<IconProps>
    /** Called when the item expanded state is toggled. */
    onChange: (itemKey: string, expanded: boolean) => void
  }>

/** Item that represents a node inside a tree. Can either be a leaf node or an intermediate
 * item, depending on wheter it has children or not
 */
const TreeItem: React.FC<TreeItemProps> = ({
  children,
  expandIcon,
  collapseIcon,
  label,
  itemKey,
  onChange,
  preview,
  highlightUpdates,
}) => {
  const previousPreview = usePrevious(preview)
  const [isShowingUpdate, setIsShowingUpdate] = useState(false)
  const timeoutIdRef = useRef(0)
  const isInitialRun = useRef(true)
  const [isExpanded, setIsExpanded] = useState(false)
  const StatusIcon = isExpanded ? collapseIcon : expandIcon

  useEffect(() => {
    if (onChange && !isInitialRun.current) {
      onChange(itemKey, isExpanded)
    }
    isInitialRun.current = false
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isExpanded])

  useLayoutEffect(() => {
    if (highlightUpdates && previousPreview && preview !== previousPreview) {
      clearTimeout(timeoutIdRef.current)
      setIsShowingUpdate(true)
      timeoutIdRef.current = window.setTimeout(() => {
        setIsShowingUpdate(false)
      }, 500)
    }
  }, [highlightUpdates, preview, previousPreview])

  const shouldRenderPreview = preview && (!children || !isExpanded)

  return (
    <div className="relative">
      {children && StatusIcon && (
        <StatusIcon
          data-testid={`${itemKey}-toggle-status-icon`}
          className="absolute -left-6 top-1 h-5 w-5 cursor-pointer text-gray-text dark:text-gray-400"
          onClick={() => setIsExpanded(!isExpanded)}
        />
      )}
      <div
        data-testid={`${itemKey}-toggle-status-label`}
        className={cx(
          'grid w-full grid-flow-col grid-cols-min-fr gap-x-4 leading-6',
          {
            ['cursor-pointer']: !!children,
          },
        )}
        onDoubleClick={
          !!children ? () => setIsExpanded(!isExpanded) : undefined
        }
      >
        <div className="text-code-label">{`${label}${
          shouldRenderPreview && !!label ? ':' : ''
        }`}</div>
        {shouldRenderPreview && (
          <div
            data-testid={`${itemKey}-preview`}
            className={cx('relative text-code-value', {
              ['truncate']: !!children && !isExpanded,
            })}
          >
            <Transition
              className="absolute left-0 top-0 h-full w-full"
              enter="transition-opacity linear duration-1000"
              leave="transition-opacity linear duration-1000"
              leaveFrom="opacity-50"
              leaveTo="opacity-0"
              enterFrom="opacity-0"
              enterTo="opacity-50"
              show={isShowingUpdate}
              as="div"
            >
              <div
                className="h-full w-full rounded-lg bg-primary-200"
                data-testid="update"
              />
            </Transition>
            {preview}
          </div>
        )}
      </div>
      {children && (
        <div
          data-testid={`${itemKey}-children`}
          className={cx('pl-4', { ['hidden']: !isExpanded })}
        >
          {React.Children.toArray(children).map(
            (child) =>
              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
              // @ts-ignore
              'props' in child &&
              React.cloneElement(child as React.ReactElement<TreeItemProps>, {
                expandIcon,
                collapseIcon,
                onChange: child.props.onChange ?? onChange,
              }),
          )}
        </div>
      )}
    </div>
  )
}

TreeItem.defaultProps = {
  highlightUpdates: true,
}

export default TreeItem
